<template>
  <transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
      aria-live="polite"
      aria-busy="true"
      @click="close"
    >
      <div class="rounded-lg bg-white shadow-lg px-6 py-4 flex items-center gap-3">
        <svg class="h-5 w-5 animate-spin text-gray-700" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>

        <div class="text-sm text-gray-800">
          <div class="font-semibold">{{ title }}</div>
          <div v-if="message" class="text-gray-600">{{ message }}</div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: 'Loading…' },
  message: { type: String, default: '' },
  autoCloseOnFocus: { type: Boolean, default: true },
  fallbackMs: { type: Number, default: 45000 },
})

const emit = defineEmits(['update:modelValue'])

let fallbackTimer = null

const close = () => emit('update:modelValue', false)

const startFallback = () => {
  if (!props.fallbackMs) return
  clearTimeout(fallbackTimer)
  fallbackTimer = setTimeout(() => {
    close()
  }, props.fallbackMs)
}

const onFocus = () => {
  if (props.autoCloseOnFocus && props.modelValue) close()
}

onMounted(() => {
  window.addEventListener('focus', onFocus)
  document.addEventListener('visibilitychange', () => {
    if (props.autoCloseOnFocus && props.modelValue && document.visibilityState === 'visible') close()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('focus', onFocus)
  clearTimeout(fallbackTimer)
})

watch(
  () => props.modelValue,
  (val) => {
    if (val) startFallback()
    else clearTimeout(fallbackTimer)
  },
  { immediate: true }
)
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>